import type { Session } from "@/features/auth/api/session.api";

/**
 * Whether the application may render at all yet.
 *
 * IN ITS OWN `.ts` FILE, away from the component that uses it. A module that exports both a
 * component and a plain function loses fast refresh — React's plugin cannot tell which half
 * changed, so editing the rule reloads the whole page and loses the screen's state. The repo
 * treats that warning as an error for exactly this reason.
 *
 * `SHARED` with several customers and none chosen is the ONLY state that blocks. A dedicated
 * installation never reaches it: its session carries exactly one reachable customer and the
 * server resolves that one silently, so nobody is ever shown a choice with one option.
 */
export function needsTenantChoice(session: Session): boolean {
  return (
    session.tenant.mode === "SHARED" &&
    session.tenant.current === null &&
    session.tenant.available.length > 1
  );
}

/**
 * Whether the person should be told, all the time, which customer they are working in.
 *
 * NOT the same question as `needsTenantChoice`, and the difference is the point. That one asks
 * whether the application may render at all; this one asks whether a chosen customer is a fact
 * worth keeping on screen afterwards.
 *
 * The answer is yes exactly when the choice was real. Somebody who reaches one customer — every
 * person on a dedicated installation, and most people on the shared service — already knows
 * where they are, and a permanent strip telling them so is the kind of chrome people learn to
 * stop reading. Somebody who reaches several is the accountant serving three small businesses,
 * and for them the cost of not knowing is posting to the wrong books.
 */
export function showsTenantContext(session: Session): boolean {
  return session.tenant.mode === "SHARED" && session.tenant.available.length > 1;
}
